import {
  CreateRepeatableTaskForm,
  EditRepeatableTaskForm,
  RepeatableTaskPeriod,
  RepeatableTasksService,
  TaskPriority,
} from '@api'
import { z } from 'zod'

import {
  BaseEditTaskSchema,
  TaskBaseSchema,
} from '@/modules/tasks/service/task-base'

export const PeriodSchema = z.enum(['Daily', 'Weekly', 'Monthly', 'Annually'])

export const RepeatableTaskSchema = TaskBaseSchema.extend({
  period: PeriodSchema.nullish(),
  rate: z.array(z.number()).nullish(),
  hourlyDueDuration: z.number().nullish(),
  next: z.string().nullish(),
  endDate: z.string().nullish(),
})

export const EditRepeatableTaskSchema = BaseEditTaskSchema.extend({
  period: z.string().nullish(),
  rate: z.array(z.number()).nullish(),
  hourlyDueDuration: z.number().nullish(),
  end: z.string().nullish(),
  start: z.string().nullish(),
})

export const weekdays = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
] as const

export type Weekday = (typeof weekdays)[number]

export type RepeatableTask = z.infer<typeof RepeatableTaskSchema>
export type RepeatableTaskForm = z.infer<typeof EditRepeatableTaskSchema>

export const ListRepeatableTaskResponseSchema = z.array(RepeatableTaskSchema)

function create(form: RepeatableTaskForm) {
  const apiForm: CreateRepeatableTaskForm = {
    name: form.name,
    detail: form.detail || undefined,
    priority: form.priority as TaskPriority | undefined,
    period: form.period as RepeatableTaskPeriod,
    rate: form.rate?.map(String) || [],
    hourlyDueDuration: form.hourlyDueDuration || 0,
    end: form.end || undefined,
    start: form.start || undefined,
    assignees: form.assignees || [],
  }

  return RepeatableTasksService.createRepeatableTask({
    requestBody: apiForm,
  }).then((response) => RepeatableTaskSchema.parse(response))
}

function _delete(id: number) {
  return RepeatableTasksService.deleteRepeatableTask({ id })
}

export function list() {
  return RepeatableTasksService.listRepeatableTasks({}).then((response) =>
    ListRepeatableTaskResponseSchema.parse(response),
  )
}

function update({ id, ...form }: Partial<RepeatableTaskForm> & { id: number }) {
  const apiForm: EditRepeatableTaskForm = {
    name: form.name,
    detail: form.detail || undefined,
    priority: form.priority as TaskPriority | undefined,
    period: form.period as RepeatableTaskPeriod | undefined,
    rate: form.rate?.map(String) || undefined,
    hourlyDueDuration: form.hourlyDueDuration || undefined,
    end: form.end || undefined,
    assignees: form.assignees || undefined,
  }

  return RepeatableTasksService.editRepeatableTask({
    id,
    requestBody: apiForm,
  }).then((response) => RepeatableTaskSchema.parse(response))
}

export const repeatableTaskService = {
  create,
  delete: _delete,
  list,
  update,
} as const

export type RepeatableTaskService = typeof repeatableTaskService
